import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

import { useAppStore } from '@/store/app-store'
import { useLoggerStore } from '@/store/logger'
import { AvailablePGEvents } from '@/types/logger.types'

interface AudienceDetectionPattern {
    audience: string
    signalCategories: Set<string>
    contraCategories: Set<string>
    triggerEvents: Set<AvailablePGEvents>
    threshold: number
    minNetCount: number
}

interface PatternState {
    signalCount: number
    contraCount: number
}

const _AUD3_PATTERN: AudienceDetectionPattern = {
    audience: 'AUD3',
    signalCategories: new Set([
        'puzzle',
        'survival',
        'hidden-object',
        'merge',
        'cards',
        'chess',
        'mahjong',
        'tower-defense',
        'strategy',
        'logic',
        'city-building-games',
        'solitaire',
        'management',
        'brain',
        'point-and-click',
        'tycoon',
        'matching',
        'blocks',
        'farm',
        'backgammon',
        'bubble-shooter',
        'match-3',
    ]),
    contraCategories: new Set([
        'obby',
        'girls',
        'kids',
        'boys',
        'color',
        'drawing',
        'dress-up',
        'fashion',
        'make-over',
    ]),
    triggerEvents: new Set<AvailablePGEvents>(['game_30s', 'playgame_5min']),
    threshold: 3,
    minNetCount: 3,
}

// Autodetection disabled; audience is only set via the `audnc` query param.
const PATTERNS: AudienceDetectionPattern[] = [
    // _AUD3_PATTERN
]

export const useAudienceDetectionStore = defineStore(
    'audience-detection',
    () => {
        const appStore = useAppStore()
        const loggerStore = useLoggerStore()

        const patternStates = ref<Record<string, PatternState>>({})
        const detectedAudiences = ref<Set<string>>(new Set())

        function evaluatePattern(pattern: AudienceDetectionPattern) {
            const state = patternStates.value[pattern.audience] || { signalCount: 0, contraCount: 0 }

            if (detectedAudiences.value.has(pattern.audience)) {
                if (
                    appStore.rawRankingParams?.audience === pattern.audience &&
                    state.signalCount - state.contraCount < pattern.minNetCount
                ) {
                    detectedAudiences.value = new Set(
                        [...detectedAudiences.value].filter((audience) => audience !== pattern.audience),
                    )
                    appStore.persistRankingParams({ audience: undefined })
                    loggerStore.logEvent({
                        event: 'custom_event',
                        eventName: 'audience_lost',
                        dimension1: pattern.audience,
                    })
                }
                return
            }

            if (
                state.signalCount >= pattern.threshold &&
                state.signalCount - state.contraCount >= pattern.minNetCount
            ) {
                detectedAudiences.value = new Set([...detectedAudiences.value, pattern.audience])
                appStore.persistRankingParams({ audience: pattern.audience })
                loggerStore.logEvent({
                    event: 'custom_event',
                    eventName: 'audience_detected',
                    dimension1: pattern.audience,
                })
            }
        }

        watch(
            patternStates,
            () => {
                for (const pattern of PATTERNS) {
                    evaluatePattern(pattern)
                }
            },
            { deep: true },
        )

        function handleGameplayEvent(eventName: AvailablePGEvents, mainCategory: string) {
            if (!appStore.isPortalEnv) {
                return
            }

            for (const pattern of PATTERNS) {
                if (!pattern.triggerEvents.has(eventName)) {
                    continue
                }

                const state = patternStates.value[pattern.audience] || { signalCount: 0, contraCount: 0 }

                if (pattern.signalCategories.has(mainCategory)) {
                    patternStates.value = {
                        ...patternStates.value,
                        [pattern.audience]: { ...state, signalCount: state.signalCount + 1 },
                    }
                } else if (pattern.contraCategories.has(mainCategory)) {
                    patternStates.value = {
                        ...patternStates.value,
                        [pattern.audience]: { ...state, contraCount: state.contraCount + 1 },
                    }
                }
            }
        }

        return {
            patternStates,
            detectedAudiences,
            handleGameplayEvent,
        }
    },
    {
        persist: {
            pick: ['patternStates'],
        },
    },
)
